import { useCallback, useRef, useState } from "react";
import { Button, Col, Form, FormInstance, Input, notification, Popconfirm, Row, Space, Table } from "antd";
import { DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

import { IOrgao, IOrgaoVinculado } from "../../../../../../Orgaos/interfaces";
import { ICreateOrgaoVinculado, IDeleteOrgaoVinculado, IUpdateOrgaoVinculado } from "./interfaces";

import { inputNomeOrgaoVinculado, rowColNomeOrgaoVinculado, rowColTableOrgaosVinculados } from "./GridStyle";
import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import { mask, unMask } from "../../../../../../../../utils/MascaraDeCampos";
import cnpjValidator from "../../../../../../../../utils/Validators/cnpjValidator";
import { removeAcento } from "../../../../../../../../utils/RemoveAcentos";
import SolicitacoesDeAtivacaoService from "../../../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import { onlyNumbers } from "../../../../../../utils";


interface IProps {
    solicitacaoId: number;
    orgao: IOrgao | null;
    onOrgaoChange: (orgao: IOrgao) => void;
    onCreateOrgaoVinculado: (data: ICreateOrgaoVinculado) => Promise<IOrgao | null>;
    onUpdateOrgaoVinculado: (data: IUpdateOrgaoVinculado) => Promise<IOrgao | null>;
    onDeleteOrgaoVinculado: (data: IDeleteOrgaoVinculado) => Promise<IOrgao | null>;
    isManutencao: boolean;
}

export default function OrgaosVinculadosStep({
    solicitacaoId,
    orgao,
    onOrgaoChange,
    onCreateOrgaoVinculado,
    onUpdateOrgaoVinculado,
    onDeleteOrgaoVinculado,
    isManutencao,
}: IProps) {
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isDeletingOrgaoVinculado, setIsDeletingOrgaoVinculado] = useState(false);
    const [isDeletingAllOrgaosVinculados, setIsDeletingAllOrgaosVinculados] = useState(false);

    const formRef = useRef<FormInstance<any>>(null);
    const [editingOrgaoVinculadoId, setEditingOrgaoVinculadoId] = useState<number | null>(null);

    const handleSearch = useCallback((selectedKeys, confirm, dataIndex): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters): void => {
        clearFilters();
        setSearchText('');
    }, []);

    const handleClickEditOrgaoVinculado = useCallback((orgaoVinculado: IOrgaoVinculado) => {
        setEditingOrgaoVinculadoId(orgaoVinculado.id);

        const maskedCnpj = mask(orgaoVinculado.cnpj, [
            "99.999.999/9999-99"
        ]);

        formRef.current?.setFieldsValue({
            nome: orgaoVinculado.nome,
            cnpj: maskedCnpj,
        })
    }, []);

    const getColumnSearchProps = useCallback((
        dataIndex,
        valueFormatterBeforeCompare?: (value: string) => string,
    ) => {
        let searchInput: any = '';
        return ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value: any, record: any) => {
                const recordValue = record[dataIndex]?.toString();

                let trecord = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(recordValue) : recordValue);

                const valueToCompare = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(value) : value);

                return record[dataIndex] ? trecord.includes(valueToCompare) : false
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput.select());
                }
            },
            render: (text: any) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        })
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'nome',
            ...getColumnSearchProps('nome'),
        },
        {
            title: 'CNPJ',
            dataIndex: 'cnpj',
            ...getColumnSearchProps('cnpj', onlyNumbers),
            render: (text: any, record: any) => {
                const maskedValue = mask(text, [
                    "99.999.999/9999-99"
                ]);

                return maskedValue;
            }
        },
        {
            title: 'Ações',
            dataIndex: 'acoes',
            width: 110,
            render: (text: any, record: IOrgaoVinculado) =>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}
                >
                    <Button
                        type="primary"
                        onClick={() => handleClickEditOrgaoVinculado(record)}
                        className="botaoEditarAssociacao"
                        size="small"
                        style={{ width: 'auto', marginLeft: 0 }}
                    >
                        <EditOutlined />
                    </Button>

                    <Popconfirm
                        title="Deseja realmente deletar o órgão vinculado?"
                        onConfirm={() => handleDeleteOrgaoVinculado(record)}
                        onCancel={() => { }}
                        okText="Sim"
                        cancelText="Não"
                        okButtonProps={{
                            loading: isDeletingOrgaoVinculado,
                            disabled: isDeletingOrgaoVinculado,
                        }}
                        cancelButtonProps={{
                            disabled: isDeletingOrgaoVinculado
                        }}
                        disabled={isDeletingOrgaoVinculado || (!!editingOrgaoVinculadoId)}
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            disabled={isDeletingOrgaoVinculado || (!!editingOrgaoVinculadoId)}
                        >
                            <DeleteOutlined />
                        </Button>
                    </Popconfirm>
                </div>
        },
    ];

    const handleClickCancelarEdicao = useCallback(() => {
        formRef.current?.resetFields();
        setEditingOrgaoVinculadoId(null);
    }, []);

    const handleChangeCnpj = useCallback((e: any) => {
        const originalValue = unMask(e.target.value);
        const maskedValue = mask(originalValue, [
            "99.999.999/9999-99"
        ]);

        formRef.current?.setFieldsValue(
            { cnpj: maskedValue }
        );

        if (maskedValue.length === 18) {
            formRef.current?.validateFields(["cnpj"])
        }
    }, []);

    const handleSubmit = useCallback(async () => {

        try {
            formRef.current?.validateFields().then(async (values: any) => {
                try {
                    if (!orgao) {
                        setIsSubmiting(true);

                        const updatedSolicitacao = await SolicitacoesDeAtivacaoService.createOrgao({
                            solicitacaoId,
                            orgaosVinculados: [{
                                nome: values.nome,
                                cnpj: values.cnpj,
                            }]
                        });

                        onOrgaoChange(updatedSolicitacao.orgao);
                        setIsSubmiting(false);
                        formRef.current?.resetFields();
                        notification.success({
                            message: 'Sucesso',
                            description:
                                'Órgão vinculado cadastrado com sucesso!',
                        });

                        return;
                    }

                    setIsSubmiting(true);

                    if (editingOrgaoVinculadoId) {
                        const updatedOrgao = await onUpdateOrgaoVinculado({
                            solicitacaoId,
                            orgaoId: orgao.id,
                            orgaoVinculadoId: editingOrgaoVinculadoId,
                            orgaoVinculado: {
                                nome: values.nome,
                                cnpj: values.cnpj,
                            }
                        });

                        if (!updatedOrgao) {
                            return;
                        }

                        onOrgaoChange(updatedOrgao);
                        setEditingOrgaoVinculadoId(null);
                        setIsSubmiting(false);
                        formRef.current?.resetFields();
                        notification.success({
                            message: 'Sucesso',
                            description:
                                'Órgão vinculado atualizado com sucesso!',
                        });

                        return;
                    }

                    const updatedOrgao = await onCreateOrgaoVinculado({
                        solicitacaoId,
                        orgaoId: orgao.id,
                        orgaoVinculado: {
                            nome: values.nome,
                            cnpj: values.cnpj,
                        }
                    });

                    if (!updatedOrgao) {
                        return;
                    }

                    onOrgaoChange(updatedOrgao);
                    setIsSubmiting(false);
                    formRef.current?.resetFields();
                    notification.success({
                        message: 'Sucesso',
                        description:
                            'Órgão vinculado cadastrado com sucesso!',
                    });
                } catch (error) {
                    setIsSubmiting(false);
                    EmitirErro(error, true, "createOrgaoVinculado_aosdjasndas0dhasd");
                }
            })
                .catch(() => {
                    setIsSubmiting(false);
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });;

        } catch (error) {
            setIsSubmiting(false);
            EmitirErro(error);
        }
    }, [orgao, solicitacaoId, editingOrgaoVinculadoId]);

    const handleDeleteOrgaoVinculado = useCallback(async (orgaoVinculado: IOrgaoVinculado) => {
        if (solicitacaoId && orgao) {
            try {
                setIsDeletingOrgaoVinculado(true);

                const updatedOrgao = await onDeleteOrgaoVinculado({
                    solicitacaoId,
                    orgaoVinculadoId: orgaoVinculado.id,
                    orgaoId: orgao?.id
                });

                if (!updatedOrgao) {
                    return;
                }

                notification.success({
                    message: 'Sucesso',
                    description: 'Órgão vinculado deletado com sucesso!',
                });

                onOrgaoChange(updatedOrgao);
                setIsDeletingOrgaoVinculado(false);

                if (editingOrgaoVinculadoId) {
                    handleClickCancelarEdicao();
                }
            } catch (error) {
                EmitirErro(error, true, 'deleteOrgaoVinculado_2304onasdasjnod023kmopkas');
                setIsDeletingOrgaoVinculado(false);
            }
        }
    }, [solicitacaoId, editingOrgaoVinculadoId, orgao]);

    const handleDeleteAllOrgaosVinculados = useCallback(async () => {
        if (solicitacaoId) {
            try {
                setIsDeletingAllOrgaosVinculados(true);

                const updatedSolicitacao = await SolicitacoesDeAtivacaoService.deleteOrgaosVinculados({
                    solicitacaoId,
                });

                notification.success({
                    message: 'Sucesso',
                    description: 'Órgãos Vinculados deletados com sucesso!',
                });

                onOrgaoChange(updatedSolicitacao.orgao);
                setIsDeletingAllOrgaosVinculados(false);

                if (editingOrgaoVinculadoId) {
                    handleClickCancelarEdicao();
                }
            } catch (error) {
                EmitirErro(error, true, 'deleteOrgaosVinculados_2304onasdasjnod023kmopkas');
                setIsDeletingAllOrgaosVinculados(false);
            }
        }
    }, [solicitacaoId, editingOrgaoVinculadoId]);

    return (
        <>
            <Row gutter={24}>
                <Col {...rowColNomeOrgaoVinculado}>
                    <Form
                        ref={formRef}
                        name="formAdicionarAssociacoes"
                        layout="vertical"
                        onFinishFailed={() => { }}
                    >
                        <Row>
                            <Col {...inputNomeOrgaoVinculado}>
                                <Form.Item
                                    name="nome"
                                    label="Nome do órgão vinculado"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor digite o nome do órgão vinculado!',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Ex: Prefeitura Municipal de Maringá - PR" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col {...inputNomeOrgaoVinculado}>
                                <Form.Item
                                    name="cnpj"
                                    label="CNPJ do órgão vinculado"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor digite o CNPJ do órgão vinculado!',
                                        },
                                        {
                                            max: 18,
                                            min: 18,
                                            message: 'CNPJ inválido',
                                            // array vazio para validar apenas no momento do submit
                                            validateTrigger: [],
                                        },
                                        () => ({
                                            validator(rule, value) {
                                                return new Promise((resolve: (value?: any) => void, reject) => {
                                                    if (value != '' && value != null) {
                                                        value = unMask(value);
                                                        if (value.length === 14) {
                                                            const isCnpjValid = cnpjValidator.validate(value);

                                                            if (isCnpjValid) {
                                                                resolve();
                                                            } else {
                                                                reject('CNPJ inválido');
                                                            }
                                                        } else {
                                                            resolve();
                                                        }
                                                    } else {
                                                        reject('');
                                                    }
                                                });
                                            },
                                        }),
                                    ]}
                                >
                                    <Input
                                        placeholder="00.000.000/0000-00"
                                        onChange={handleChangeCnpj}
                                        autoComplete="off"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row style={{ justifyContent: 'flex-end' }} >
                            <Col style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {
                                    editingOrgaoVinculadoId && (
                                        <Button
                                            onClick={handleClickCancelarEdicao}
                                            disabled={isSubmiting}
                                        >
                                            Cancelar edição
                                        </Button>
                                    )
                                }

                                <Button
                                    type="primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmiting}
                                    loading={isSubmiting}
                                >
                                    {editingOrgaoVinculadoId ? 'Atualizar' : 'Adicionar'} órgão vinculado
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>

                <Col {...rowColTableOrgaosVinculados}>
                    <Table
                        rowKey={(record) => record.id}
                        style={{ marginTop: 6 }}
                        className="tabelaAssociacaoAdd"
                        size="small"
                        columns={columns}
                        dataSource={orgao?.orgaosVinculados || []}
                        scroll={{ y: 400 }}
                        pagination={{ locale: { items_per_page: '' } }}
                        footer={!isManutencao ? () => (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end'
                                }}
                            >
                                {
                                    (orgao?.orgaosVinculados.length || 0) > 0 && (
                                        <Popconfirm
                                            title="Deseja realmente deletar TODOS os Órgão Vinculados?"
                                            onConfirm={handleDeleteAllOrgaosVinculados}
                                            onCancel={() => { }}
                                            okText="Sim"
                                            cancelText="Não"
                                            okButtonProps={{
                                                loading: isDeletingAllOrgaosVinculados,
                                                disabled: isDeletingAllOrgaosVinculados,
                                            }}
                                            cancelButtonProps={{
                                                disabled: isDeletingAllOrgaosVinculados
                                            }}
                                            disabled={isDeletingAllOrgaosVinculados || (!!editingOrgaoVinculadoId)}
                                        >
                                            <Button
                                                danger
                                                ghost
                                                disabled={isDeletingAllOrgaosVinculados || (!!editingOrgaoVinculadoId)}
                                            >
                                                Deletar todos
                                            </Button>
                                        </Popconfirm>
                                    )
                                }
                            </div>
                        ) : undefined}
                    />
                </Col>
            </Row>
        </>
    )
}
